import jsPDF from "jspdf";
import html2canvas from "html2canvas";

/**
 * Generate PDF from HTML element
 * @param {HTMLElement} element - The HTML element to convert to PDF
 * @param {Object} data - Letter data (used for filename)
 * @returns {Promise<Blob>} - PDF as Blob
 */
export const generatePDF = async (element, data) => {
    if (!element) {
        throw new Error("Element not found");
    }

    try {
        // Create a temporary container with A4 dimensions
        const tempContainer = document.createElement("div");
        tempContainer.style.position = "absolute";
        tempContainer.style.left = "-9999px";
        tempContainer.style.top = "0";
        tempContainer.style.width = "210mm"; // A4 width
        tempContainer.style.backgroundColor = "#ffffff";
        
        // Clone the element
        const clonedElement = element.cloneNode(true);
        tempContainer.appendChild(clonedElement);
        document.body.appendChild(tempContainer);

        // Convert HTML to canvas with higher scale for quality
        const canvas = await html2canvas(clonedElement, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff",
            windowWidth: 794, // 210mm in pixels at 96 DPI
        });

        // Remove temporary container
        document.body.removeChild(tempContainer);

        // Get canvas image data
        const imgData = canvas.toDataURL("image/jpeg", 0.85);

        // Create PDF in A4 size
        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth(); // 210mm
        const pdfHeight = pdf.internal.pageSize.getHeight(); // 297mm

        // 1 inch margins only for top and bottom = 25.4mm
        const marginTop = 25.4;
        const marginBottom = 25.4;
        const marginLeft = 0;  // No left margin
        const marginRight = 0; // No right margin

        const availableWidth = pdfWidth; // Full width (210mm)
        const availableHeight = pdfHeight - marginTop - marginBottom; // 246.2mm

        // Calculate image dimensions
        const imgProps = pdf.getImageProperties(imgData);
        let imgWidth = availableWidth;
        let imgHeight = (imgProps.height * availableWidth) / imgProps.width;

        // Scale down if content exceeds available height
        if (imgHeight > availableHeight) {
            imgHeight = availableHeight;
            imgWidth = (imgProps.width * availableHeight) / imgProps.height;
        }

        // Center horizontally if needed (though with full width it shouldn't be necessary)
        const xOffset = (pdfWidth - imgWidth) / 2;

        // Add image to PDF with only top margin
        pdf.addImage(
            imgData,
            "JPEG",
            xOffset,
            marginTop,
            imgWidth,
            imgHeight
        );

        // Convert to Blob
        const pdfBlob = pdf.output("blob");

        return pdfBlob;
    } catch (error) {
        console.error("Error generating PDF:", error);
        throw new Error("Failed to generate PDF");
    }
};

/**
 * Download PDF directly (alternative method)
 * @param {HTMLElement} element - The HTML element to convert to PDF
 * @param {string} filename - Name of the PDF file
 */
export const downloadPDF = async (element, filename = "document.pdf") => {
    if (!element) {
        throw new Error("Element not found");
    }

    try {
        const canvas = await html2canvas(element, {
            scale: 3,
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff",
        });

        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        const imgProps = pdf.getImageProperties(imgData);
        const imgWidth = pdfWidth;
        const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

        const scaleFactor = imgHeight > pdfHeight ? pdfHeight / imgHeight : 1;

        pdf.addImage(
            imgData,
            "PNG",
            0,
            0,
            imgWidth * scaleFactor,
            imgHeight * scaleFactor
        );

        pdf.save(filename);
    } catch (error) {
        console.error("Error downloading PDF:", error);
        throw new Error("Failed to download PDF");
    }
};